import { CheckCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import type { PurchaseItemRow, PurchaseRow, SupplierDeliveryRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayDate, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Drawer,
  Segmented,
  Space,
  Spin,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../session';

const PAGE_SIZE = 20;

/** `wl_purchase_order.order_type`（docs/db/wz-biz/10-wl-request.sql）。 */
const ORDER_TYPE_META: Record<string, string> = {
  NORMAL: '普通采购',
  EMERGENCY: '紧急采购',
  FRAMEWORK: '框架协议',
  THIRD_PARTY: '第三方代采'
};

const ORDER_STATUS: Record<string, { text: string; color: string }> = {
  SENT: { text: '待接单', color: 'gold' },
  ACKED: { text: '已接单', color: 'blue' },
  PART_RECEIVED: { text: '部分到货', color: 'orange' },
  RECEIVED: { text: '已到货', color: 'green' },
  CLOSED: { text: '已关闭', color: 'default' },
  CANCELED: { text: '已取消', color: 'default' },
  TERMINATED: { text: '已终止', color: 'default' }
};

type StatusFilter = 'ALL' | 'SENT' | 'ACKED' | 'RECEIVED';

/** `wl_delivery.delivery_status`（docs/db/wz-biz/12-wl-logistics.sql）。 */
const DELIVERY_STATUS: Record<string, { text: string; color: string }> = {
  NEW: { text: '待发运', color: 'default' },
  CREATED: { text: '已生成', color: 'default' },
  PICKING: { text: '拣货中', color: 'blue' },
  PACKED: { text: '已打包', color: 'blue' },
  SHIPPED: { text: '运输中', color: 'gold' },
  ARRIVED: { text: '已到达', color: 'orange' },
  PART_SIGNED: { text: '部分签收', color: 'orange' },
  SIGNED: { text: '已签收', color: 'green' }
};

/** 订单协同：查看院方下达的采购订单（CG 单）并确认接单；到货由院方入库确认。 */
export default function OrderSupplier() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [current, setCurrent] = useState<PurchaseRow | null>(null);

  const list = useQuery({
    queryKey: ['supplier', 'purchase', status],
    placeholderData: keepPreviousData,
    queryFn: () =>
      hq.procurement.purchases.list({
        size: PAGE_SIZE,
        orderStatus: status === 'ALL' ? undefined : status
      })
  });

  const detail = useQuery({
    queryKey: ['supplier', 'purchase', 'detail', current?.id],
    placeholderData: keepPreviousData,
    queryFn: () => hq.procurement.purchases.detail(current?.id ?? ''),
    enabled: Boolean(current?.id)
  });

  const ack = useMutation({
    mutationFn: (id: string) => hq.procurement.purchases.ack(id),
    onSuccess: async (data) => {
      message.success(`已接单：${data.order.bizNo}`);
      setCurrent(data.order);
      await queryClient.invalidateQueries({ queryKey: ['supplier', 'purchase'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '接单失败')
  });

  // 供应商自发货（`delivery_mode=SELF`）：只回传运输节点，配送单范围由后端按 `sid` 收口。
  const deliveries = useQuery({
    queryKey: ['supplier', 'deliveries'],
    queryFn: () => hq.track.supplierDeliveries()
  });

  const reportTrack = useMutation({
    mutationFn: (input: { id: SupplierDeliveryRow['id']; type: 'SHIP' | 'ARRIVE' | 'EXCEPTION' }) =>
      hq.track.report({
        deliveryId: input.id,
        trackType: input.type,
        trackDesc: input.type === 'EXCEPTION' ? '供应商上报运输异常' : undefined,
        terminal: 'PC'
      }),
    onSuccess: async () => {
      message.success('物流节点已回传');
      await queryClient.invalidateQueries({ queryKey: ['supplier', 'deliveries'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '回传失败')
  });

  const deliveryColumns: ColumnsType<SupplierDeliveryRow> = [
    { title: '配送单号', dataIndex: 'bizNo', width: 190 },
    { title: '去向科室', dataIndex: 'toDeptName', width: 140, render: (value: string | null) => value || '-' },
    {
      title: '状态',
      dataIndex: 'deliveryStatus',
      width: 110,
      render: (value: string) => {
        const meta = DELIVERY_STATUS[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '发货时间', dataIndex: 'shipAt', width: 150, render: (value: string | null) => formatDisplayTime(value) },
    { title: '到达时间', dataIndex: 'arriveAt', width: 150, render: (value: string | null) => formatDisplayTime(value) },
    {
      title: '操作',
      width: 190,
      render: (_, row) => (
        <Space size={4}>
          <Button
            size="small"
            type="primary"
            disabled={row.deliveryStatus === 'SHIPPED' || row.deliveryStatus === 'ARRIVED'}
            loading={reportTrack.isPending}
            onClick={() => reportTrack.mutate({ id: row.id, type: 'SHIP' })}
          >
            发货
          </Button>
          <Button
            size="small"
            disabled={row.deliveryStatus !== 'SHIPPED'}
            loading={reportTrack.isPending}
            onClick={() => reportTrack.mutate({ id: row.id, type: 'ARRIVE' })}
          >
            到达
          </Button>
          <Button
            size="small"
            danger
            loading={reportTrack.isPending}
            onClick={() => reportTrack.mutate({ id: row.id, type: 'EXCEPTION' })}
          >
            异常
          </Button>
        </Space>
      )
    }
  ];

  const columns: ColumnsType<PurchaseRow> = [
    {
      title: '采购单号',
      dataIndex: 'bizNo',
      width: 200,
      render: (value: string, row) => <Typography.Link onClick={() => setCurrent(row)}>{value}</Typography.Link>
    },
    {
      title: '类型',
      dataIndex: 'orderType',
      width: 110,
      render: (value: string | null) => (value ? ORDER_TYPE_META[value] ?? value : '-')
    },
    { title: '数量', dataIndex: 'totalQty', width: 110, align: 'right', render: (value: number) => formatQty(value) },
    {
      title: '金额',
      dataIndex: 'totalAmount',
      width: 140,
      align: 'right',
      render: (value: number) => formatAmount(value)
    },
    { title: '期望到货', dataIndex: 'expectArriveAt', width: 150, render: (value: string | null) => formatDisplayTime(value) },
    { title: '接单时间', dataIndex: 'supplierAckAt', width: 150, render: (value: string | null) => formatDisplayTime(value) },
    {
      title: '状态',
      dataIndex: 'orderStatus',
      width: 110,
      render: (value: string) => {
        const meta = ORDER_STATUS[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    }
  ];

  const itemColumns: ColumnsType<PurchaseItemRow> = [
    { title: '行', dataIndex: 'lineNo', width: 60 },
    { title: '物资编码', dataIndex: 'materialCode', width: 150, render: (value: string | null) => value || '-' },
    { title: '物资名称', dataIndex: 'materialName', ellipsis: true },
    { title: '规格', dataIndex: 'spec', width: 140, render: (value: string | null) => value || '-' },
    { title: '单位', dataIndex: 'unitName', width: 80, render: (value: string | null) => value || '-' },
    { title: '数量', dataIndex: 'qty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '单价', dataIndex: 'price', width: 120, align: 'right', render: (value: number) => formatAmount(value, 4) },
    { title: '金额', dataIndex: 'amount', width: 130, align: 'right', render: (value: number) => formatAmount(value) }
  ];

  const canAck = detail.data?.order.orderStatus === 'SENT';

  return (
    <Card
      className="page-card"
      title={
        <Space>
          订单协同
          <Typography.Text type="secondary">院方下达的采购订单在此确认接单</Typography.Text>
        </Space>
      }
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => list.refetch()} loading={list.isFetching}>
          刷新
        </Button>
      }
    >
      <Segmented<StatusFilter>
        value={status}
        options={[
          { label: '全部', value: 'ALL' },
          { label: '待接单', value: 'SENT' },
          { label: '已接单', value: 'ACKED' },
          { label: '已到货', value: 'RECEIVED' }
        ]}
        onChange={(value) => setStatus(value)}
        style={{ marginBottom: 12 }}
      />

      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="采购订单加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}

      <Table<PurchaseRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={list.data?.items ?? []}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: 1000 }}
        locale={{ emptyText: '暂无采购订单' }}
        onRow={(row) => ({ onDoubleClick: () => setCurrent(row), style: { cursor: 'pointer' } })}
      />

      <Card
        size="small"
        style={{ marginTop: 16 }}
        title={
          <Space>
            自发货回传
            <Typography.Text type="secondary">配送模式为供应商自送时，在此回传发货/到达/异常</Typography.Text>
          </Space>
        }
        extra={
          <Button size="small" icon={<ReloadOutlined />} onClick={() => deliveries.refetch()} loading={deliveries.isFetching}>
            刷新
          </Button>
        }
      >
        {deliveries.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message="配送单加载失败"
            description={
              deliveries.error instanceof ApiError ? deliveries.error.friendlyMessage : String(deliveries.error)
            }
          />
        ) : null}
        <Table<SupplierDeliveryRow>
          rowKey="id"
          size="small"
          columns={deliveryColumns}
          dataSource={deliveries.data ?? []}
          loading={deliveries.isPending}
          pagination={false}
          scroll={{ x: 900 }}
          locale={{ emptyText: '暂无待回传的配送单' }}
        />
      </Card>

      <Drawer
        title={current?.bizNo ?? '采购订单'}
        open={current !== null}
        onClose={() => setCurrent(null)}
        width={900}
        extra={
          canAck ? (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={ack.isPending}
              onClick={() =>
                modal.confirm({
                  title: '确认接单？',
                  content: '接单后请按约定时效备货送达；到货数量以院方入库确认为准。',
                  okText: '确认接单',
                  cancelText: '取消',
                  onOk: () => ack.mutateAsync(current?.id ?? '')
                })
              }
            >
              确认接单
            </Button>
          ) : null
        }
      >
        {detail.isLoading ? (
          <Spin />
        ) : detail.isError ? (
          <Alert
            type="error"
            showIcon
            message="详情加载失败"
            description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
          />
        ) : detail.data ? (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="配送模式决定由谁回传物流节点"
              description="平台统一配送时由承运/仓储在「配送轨迹」回传；供应商自送（SELF）时在本页「自发货回传」登记发货、到达与异常。"
            />
            <Descriptions size="small" column={3} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="状态">
                {ORDER_STATUS[detail.data.order.orderStatus]?.text ?? detail.data.order.orderStatus}
              </Descriptions.Item>
              <Descriptions.Item label="下单日期">{formatDisplayDate(detail.data.order.bizDate)}</Descriptions.Item>
              <Descriptions.Item label="期望到货">{formatDisplayTime(detail.data.order.expectArriveAt)}</Descriptions.Item>
              <Descriptions.Item label="数量合计">{formatQty(detail.data.order.totalQty)}</Descriptions.Item>
              <Descriptions.Item label="金额合计">{formatAmount(detail.data.order.totalAmount)}</Descriptions.Item>
              <Descriptions.Item label="已到货">
                {formatQty(detail.data.order.receivedQty)}
              </Descriptions.Item>
              <Descriptions.Item label="备注" span={3}>
                {detail.data.order.remark || '-'}
              </Descriptions.Item>
            </Descriptions>
            <Table<PurchaseItemRow>
              rowKey="id"
              size="small"
              columns={itemColumns}
              dataSource={detail.data.items}
              pagination={false}
              scroll={{ x: 1000 }}
            />
          </>
        ) : null}
      </Drawer>
    </Card>
  );
}
